import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api-client';

/**
 * Admin dashboard summary — mirrors GET /api/dashboard/summary.
 * Entity-scoped when `entityId` is supplied; tenant-wide otherwise.
 */
export type DashboardSummary = {
  entity_id: string | null;
  fiscal_year: string;
  headcount: { active: number; total: number };
  latest_run: {
    id: string;
    status: string;
    run_type: string;
    period_label: string;
    payment_date: string | null;
    employee_count: number;
    total_gross: number;
    total_net: number;
    total_deductions: number;
    total_tds: number;
  } | null;
  approvals: {
    reimbursements_pending: number;
    reimbursements_awaiting_payout: number;
    it_declarations_pending: number;
    ctc_revisions_pending: number;
    arrears_computed: number;
    total: number;
  };
  compliance: {
    /** Challan batches (TDS deducted) with no deposit recorded yet, and how many are past the deposit due date. */
    challans_pending: number;
    challans_overdue: number;
    /** Quarterly TDS statements (one per TAN) for ended quarters not yet filed, and how many are past due. */
    returns_pending: number;
    returns_overdue: number;
  };
};

export function useDashboardSummary(entityId?: string | null) {
  const qs = entityId ? `?entity_id=${encodeURIComponent(entityId)}` : '';
  return useQuery({
    queryKey: ['dashboard', 'summary', entityId ?? 'all'],
    queryFn: () => api.get<DashboardSummary>(`/dashboard/summary${qs}`),
    staleTime: 60 * 1000,
  });
}
